import React, { useState, useEffect, useCallback } from 'react';
import PasswortRegeln from '../passwort/PasswortRegeln';
import { statusText, TURNIER_STATUS } from '../../lib/turnierStatus';
import { useNavigate, useLocation } from 'react-router-dom';
import PushSchalter from '../PushSchalter';
import { useTranslation } from 'react-i18next';
import { useVerband } from '../../context/VerbandContext';
import LanguageSwitch from '../LanguageSwitch';
import WettkaempferRegistrieren from './WettkaempferRegistrieren';
import VereineListePage from '../../pages/Club/VereineListePage';
import VereinDetailPage from '../../pages/Club/VereinDetailPage';
import VereinCreatePage from '../../pages/Club/VereinCreatePage';
import TurnierBearbeitenPage from '../../pages/Turniere/TurnierBearbeitenPage';
import TurnierAssistent from '../../pages/Turniere/TurnierAssistent';
// import TurnierListe from '../turnier/TurnierListe';
// import LiveStreamingManager from '../streaming/LiveStreamingManager';
import SportsManager from '../sports/SportsManager';
import TurnierEinstellungen from '../../pages/TurnierEinstellungen/TurnierEinstellungenPage';
import './MainDashboard.css';
import { CURRENT_VERSION, CURRENT_BUILD_DATE } from '../../version';
import { CHANGELOG } from '../../changelog';
import { toast } from '../common/Toast';

const WettkaempferDashboard       = React.lazy(() => import('./WettkaempferDashboard'));
const BracketDashboard            = React.lazy(() => import('./BracketDashboard'));
const TurnierDashboard            = React.lazy(() => import('./TurnierDashboard'));
const StreamingDashboard          = React.lazy(() => import('./StreamingDashboard'));
const AnmeldeDashboard            = React.lazy(() => import('./AnmeldeDashboard'));
const EinzelstarterDashboard      = React.lazy(() => import('./EinzelstarterDashboard'));
const VereinsmailsDashboard       = React.lazy(() => import('./VereinsmailsDashboard'));
const AnalyticsDashboard          = React.lazy(() => import('./AnalyticsDashboard'));
const DivisionDashboard           = React.lazy(() => import('./DivisionDashboard'));
// Verwaltung › Formenkatalog (23.10.2026): ersetzt das Prüfwerkzeug FormenKatalogPruefung
const FormenkatalogPage           = React.lazy(() => import('../../pages/Formenkatalog/FormenkatalogPage'));
const FormenDashboard             = React.lazy(() => import('./FormenDashboard'));
const SelbstverteidigungDashboard = React.lazy(() => import('./SelbstverteidigungDashboard'));
const MattenWettkampfDashboard    = React.lazy(() => import('./MattenWettkampfDashboard'));
const BruchtestDashboard          = React.lazy(() => import('./BruchtestDashboard'));
const KampfflaechenManager        = React.lazy(() => import('./KampfflaechenManager'));
const StaffManager                = React.lazy(() => import('./StaffManager'));
const ZeitplanDashboard           = React.lazy(() => import('./ZeitplanDashboard'));
const EventsDashboard             = React.lazy(() => import('./EventsDashboard'));
const BerichteDashboard           = React.lazy(() => import('./BerichteDashboard'));
const LiveSpectatorDashboard      = React.lazy(() => import('./LiveSpectatorDashboard'));
const ErgebnisDashboard           = React.lazy(() => import('./ErgebnisDashboard'));
const SaisonRanglisteDashboard    = React.lazy(() => import('./SaisonRanglisteDashboard'));
const EinwaageDashboard           = React.lazy(() => import('./EinwaageDashboard'));
const CheckinDashboard            = React.lazy(() => import('./CheckinDashboard'));
const AkuendigungenDashboard      = React.lazy(() => import('./AkuendigungenDashboard'));
const AkkreditierungDashboard     = React.lazy(() => import('./AkkreditierungDashboard'));
const DruckDashboard              = React.lazy(() => import('./DruckDashboard'));
const SchnellregistrierungModal   = React.lazy(() => import('./SchnellregistrierungModal'));
const RegStaffManager             = React.lazy(() => import('./RegStaffManager'));
const KampfrichterManager         = React.lazy(() => import('./KampfrichterManager'));
const RefereeManager             = React.lazy(() => import('../referee/RefereeManager'));
const WeighStaffManager           = React.lazy(() => import('./WeighStaffManager'));
const VerbaendeDashboard          = React.lazy(() => import('./VerbaendeDashboard'));
const VeranstalterAbrechnung      = React.lazy(() => import('../veranstalterAbrechnung/VeranstalterAbrechnung'));
const ModusUebersichtDashboard    = React.lazy(() => import('./ModusUebersichtDashboard'));
const PlatzierungDashboard        = React.lazy(() => import('./PlatzierungDashboard'));

const LS_VERSION_KEY = 'tda-version-seen';

// Mapping: alte activeView-IDs → BerichteDashboard report-IDs
const BERICHT_VIEW_MAP = {
  'bericht-mitgliedschaft':       'vereine',
  'bericht-offizielle':           'offizielle',
  'event-schule-bericht':         'vereine',
  'staff-checkin-bericht':        'staff',
  'staff-aktivitaetenbericht':    'staff',
  // 'kampfrichter-manager' gehört zur Personal-Seite (unten), nicht zu den
  // Berichten: sonst standen auf /dashboard/kampfrichter-manager beide.
  'rings-berichte':               'ringe',
  'bericht-wettkaempfer':         'neue-wettkaempfer',
  'teilnehmerbericht':            'teilnehmer',
  'restrictions-bericht':         'divisionen',
  'medizinisches-formular-bericht': 'medizinisch',
  'medizinische-notizen-bericht': 'medizinisch',
  'finanzbericht':                'finanzen',
  'bericht-ergebnisse':           'ergebnisse',
  'berichte':                     null,
  'medaillenbericht':             'medaillen',
  'bericht-teilzahlungen':        'teilzahlungen',
};

function MainDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { verband, activeVerband, setActiveVerband } = useVerband() || {};

  // Bestimme activeView aus der URL
  const getViewFromPath = () => {
    const path = location.pathname.replace('/dashboard', '').replace('/', '');
    return path || 'overview';
  };

  const [activeView, setActiveView] = useState(getViewFromPath());
  const [modusNavState] = useState(null); // { initialTab, initialModus }
  const [user, setUser] = useState(null);
  // Zwei-Faktor-Stand des eigenen Kontos, für den Hinweis oben (26.09.2026).
  const [zweiFaktor, setZweiFaktor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  // Handy: die Kopf-Knöpfe liegen hinter einem Menü-Knopf (18.10.2026)
  const [showKopfMenue, setShowKopfMenue] = useState(false);
  const [showVerbandDropdown, setShowVerbandDropdown] = useState(false);
  const [allVerbaende, setAllVerbaende] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const [helpTab, setHelpTab] = useState('hilfe');
  const [staffTab, setStaffTab] = useState('personal');
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [selectedTurnier, setSelectedTurnier] = useState(null);
  const [events, setEvents] = useState([]);   // Lehrgänge/Seminare/Prüfungen/Camps
  const [turniere, setTurniere] = useState([]);
  // Verhindert null-Reset von selectedTurnier beim ersten Verband-Load
  const verbandInitializedRef = React.useRef(false);
  // Verhindert parallele/doppelte fetchTurniere-Aufrufe
  const fetchingRef = React.useRef(false);
  const [ungeleseneAnnouncements, setUngeleseneAnnouncements] = useState(0);

  // Theme: dark | light (Washi)
  const [theme, setTheme] = useState(() => localStorage.getItem("tda-theme") || "light");

  // Theme auf HTML-Element setzen + in localStorage speichern
  useEffect(() => {
    if (!user) return;
    fetch('/api/2fa/status')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setZweiFaktor(d))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("tda-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === "dark" ? "light" : "dark");

  // Funktion zum Wechseln der Ansicht mit URL-Update
  const changeView = useCallback((view) => {
    setActiveView(view);
    const path = view === 'overview' ? '/dashboard' : `/dashboard/${view}`;
    navigate(path);
  }, [navigate]);

  // Synchronisiere activeView mit URL-Änderungen (z.B. Zurück-Button)
  useEffect(() => {
    const path = location.pathname.replace('/dashboard', '').replace('/', '');
    const viewFromPath = path || 'overview';
    setActiveView(prev => prev !== viewFromPath ? viewFromPath : prev);
  }, [location.pathname]);

  const handleLogout = useCallback(() => {
      ['token', 'authToken', 'adminData', 'clubData'].forEach(k => localStorage.removeItem(k));
    setUser(null);
    navigate('/');
  }, [navigate]);

  // Formen-Schreiber in neuem Fenster öffnen
  const selectedTurnierId = selectedTurnier?.turnier_id ?? selectedTurnier?.id ?? null;
  const openFormenWindow = useCallback(() => {
    if (!selectedTurnierId) {
      toast.info(t('admin.common.select_tournament_first'));
      return;
    }
    const url = `/formen-schreiber?turnierId=${selectedTurnierId}`;
    window.open(url, 'FormenSchreiber', 'width=1600,height=900,menubar=no,toolbar=no,location=no,status=no');
  }, [selectedTurnierId, t]);

  const checkAuthStatus = useCallback(() => {
    try {
      const adminData = localStorage.getItem('adminData');
      const clubData = localStorage.getItem('clubData');
      const token = localStorage.getItem('token');

      // Check if token exists (accept both JWT and dummy tokens)
      if (!token || token.trim() === '') {
        console.log('🔑 Invalid or missing token - clearing localStorage');
        handleLogout();
        return;
      }

      // Accept both JWT format and dummy tokens (admin-auth-token, user-auth-token)
      const isJWT = token.split('.').length === 3;
      const isDummyToken = token.startsWith('admin-auth-token') || token.startsWith('user-auth-token');

      if (!isJWT && !isDummyToken) {
        console.log('🔑 Invalid token format - clearing localStorage');
        handleLogout();
        return;
      }

      if (adminData) {
        const admin = JSON.parse(adminData);
        const adminUser = { ...admin.user, isAdmin: true };
        setUser(adminUser);
        // Verbände für Super-Admin laden
        if (adminUser.verband_id_tenant === null || adminUser.verband_id_tenant === undefined) {
          fetch('/api/verbaende', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
            .then(r => r.json())
            .then(d => { if (d.success) setAllVerbaende(d.data); })
            .catch(() => {});
        }
      } else if (clubData) {
        const club = JSON.parse(clubData);
        // Verein-Nutzer gehören nicht ins Admin-Dashboard
        if (club.user?.role !== 'admin') {
          navigate('/verein', { replace: true });
          return;
        }
        setUser({ ...club.user, isAdmin: false });
      } else {
        navigate('/');
        return;
      }
      // setLoading(false) wird von fetchTurniere() übernommen
      // damit User + Turnierdaten in einem einzigen Render erscheinen
    } catch (error) {
      console.error('Auth-Fehler:', error);
      handleLogout();
    }
  }, [navigate, handleLogout]);

  useEffect(() => {
    checkAuthStatus();
    fetchTurniere();  // Initialer Einmal-Load beim Mount
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/ankuendigungen', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => { if (d.success) setUngeleseneAnnouncements(d.ungelesen || 0); })
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Events (Lehrgänge/Camps) für Header-Dropdown laden
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/events', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.success) setEvents(d.data || []); })
      .catch(() => {});
  }, []);

  const fetchTurniere = useCallback(async () => {
    if (fetchingRef.current) return; // Doppel-Aufruf verhindern
    fetchingRef.current = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/turniere', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        const list = result.success && result.data ? result.data : Array.isArray(result) ? result : null;
        if (list) {
          setTurniere(list);
          // Die Liste enthält keine archivierten Turniere (GET /api/turniere
          // filtert archiviert_am). Steht die Auswahl oder die gespeicherte
          // Nummer nicht mehr darin, ist das Turnier archiviert oder gelöscht:
          // Auswahl lösen statt es im Kopf stehen zu lassen (19.10.2026).
          const inListe = (id) => id != null && list.some(t => String(t.turnier_id || t.id) === String(id));
          const savedId = localStorage.getItem('tda-selected-turnier-id');
          if (savedId && !inListe(savedId)) localStorage.removeItem('tda-selected-turnier-id');
          // Funktionales Update: nur neue Referenz setzen wenn sich die ID wirklich geändert hat.
          // Verhindert unnötige Re-Renders aller Kind-Komponenten bei gleichem Turnier.
          setSelectedTurnier(prev => {
            if (prev && inListe(prev.turnier_id || prev.id)) return prev; // Bereits gesetzt und noch da
            if (list.length === 0) return null;
            // Zuletzt gewähltes Turnier aus localStorage wiederherstellen
            if (savedId) {
              const saved = list.find(t => String(t.turnier_id || t.id) === savedId);
              if (saved) return saved;
            }
            // Kein gespeichertes → erstes kommendes Turnier
            const today = new Date(); today.setHours(0,0,0,0);
            const upcoming = list.find(t => new Date(t.datum) >= today);
            return upcoming || list[0] || null;
          });
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Turniere:', error);
    } finally {
      // Erst hier loading=false: User + Turnierdaten erscheinen gemeinsam in einem Render
      setLoading(false);
      fetchingRef.current = false; // Freigeben für nächsten Aufruf (z.B. Verband-Wechsel)
    }
  }, []);

  // Archivieren, Zurückholen oder Löschen in der Turnierverwaltung meldet
  // sich per Ereignis: Kopf-Auswahl neu laden, damit ein archiviertes Turnier
  // nicht ausgewählt bleibt.
  useEffect(() => {
    const neuLaden = () => fetchTurniere();
    window.addEventListener('tda-turniere-geaendert', neuLaden);
    return () => window.removeEventListener('tda-turniere-geaendert', neuLaden);
  }, [fetchTurniere]);

  // Verband-Wechsel: beim ERSTEN Load (verbandInitializedRef=false) kein zweiter fetchTurniere-Aufruf,
  // da der initiale useEffect ihn bereits startet. Nur bei echtem Wechsel reset+reload.
  useEffect(() => {
    if (activeVerband?.id) {
      if (verbandInitializedRef.current) {
        // Echter Verband-Wechsel: Turnier zurücksetzen und neu laden
        setSelectedTurnier(null);
        fetchTurniere();
      }
      verbandInitializedRef.current = true;
      // Kein fetchTurniere() beim ersten Verband-Load — initial useEffect übernimmt das
    }
  }, [activeVerband?.id, fetchTurniere]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Wenn ein anderer Tab adminData löscht (z.B. Vereins-Login), sofort zur Login-Seite
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'adminData' && event.newValue === null) {
        setUser(null);
        navigate('/');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate]);

  // "Neu"-Badge: prüfen ob User neue Version noch nicht gesehen hat
  useEffect(() => {
    const seen = localStorage.getItem(LS_VERSION_KEY);
    if (seen !== CURRENT_VERSION) setHasNewVersion(true);
  }, []);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">⏳</div>
        <p>{t('admin.header.loading')}</p>
      </div>
    );
  }

  return (
    <div className="main-dashboard">
      {/* COMPLETELY REMOVE ALL OLD HEADERS - ONLY ONE UNIFIED HEADER */}
      <div className="unified-dashboard-header">
        <div className="header-content">

          {/* Links: Logo + Version */}
          <div className="header-brand">
            <div className="header-logo-circle">
              <img src={verband?.logo_url || '/logo.webp'} alt={verband?.name || 'Tournament Software'} className="header-logo" />
            </div>
            <span className="header-version">v{CURRENT_VERSION}</span>
          </div>

          {/* Mitte: nur noch Verband-Switcher für Super-Admin */}
          <div className="header-center">
            {user?.verband_id_tenant === null && allVerbaende.length > 0 && (
              <div className="header-verband-switcher" style={{ position: 'relative' }}>
                <button className="header-pill-btn verband-switcher-btn" onClick={() => setShowVerbandDropdown(!showVerbandDropdown)}>
                  <span style={{ fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.02em', color: 'var(--text-muted, #9a8a6a)' }}>{activeVerband?.name || verband?.name || 'TDA'} ▾</span>
                </button>
                {showVerbandDropdown && (
                  <div className="verband-switcher-dropdown" onClick={() => setShowVerbandDropdown(false)}>
                    {allVerbaende.map(v => (
                      <div key={v.id}
                        className={`verband-switcher-item${activeVerband?.id === v.id ? ' active' : ''}`}
                        onClick={() => {
                          if (setActiveVerband) setActiveVerband(v);
                          localStorage.setItem('activeVerbandId', v.id);
                        }}>
                        <span>{v.name}</span>
                        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          {Number(v.is_demo) === 1 && <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.3rem', background: '#78350f', color: '#fbbf24', borderRadius: '3px' }}>DEMO</span>}
                          {activeVerband?.id === v.id && <span style={{ color: 'var(--accent, #3b82f6)' }}>✓</span>}
                        </span>
                      </div>
                    ))}
                    <div className="verband-switcher-divider" />
                    <div className="verband-switcher-item" onClick={() => { changeView('verbaende'); }}>
                      🌐 Verbände verwalten
                    </div>
                    <div className="verband-switcher-item" onClick={() => { changeView('veranstalter-abrechnung'); }}>
                      Veranstalter-Abrechnung
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Handy: Menü-Knopf statt fünf gestapelter Knöpfe */}
          <button
            type="button"
            className="md-menue-knopf"
            aria-label="Menü"
            aria-expanded={showKopfMenue}
            onClick={() => setShowKopfMenue(o => !o)}
          >☰</button>

          {/* Rechts: Aktions-Buttons */}
          <div className={`header-actions${showKopfMenue ? ' header-actions--offen' : ''}`} onClick={e => { if (e.target.closest('button') && !e.target.closest('.lang-switch')) setShowKopfMenue(false); }}>
            <button className="header-pill-btn header-pill-btn--red" onClick={() => changeView('overview')}>
              {t('admin.header.btn_dashboard')}
            </button>
            {user?.verband_id_tenant === null && (
              <button className="header-pill-btn header-pill-btn--admin" onClick={() => navigate('/admin')} title="Admin Plattform">
                管理
              </button>
            )}
            <button className="header-pill-btn" onClick={toggleTheme} title={t('admin.header.theme_toggle')}>
              {theme === 'dark' ? t('admin.header.theme_light') : t('admin.header.theme_dark')}
            </button>
            <LanguageSwitch />
            <button className="help-btn" onClick={() => setShowHelp(true)} title="Hilfe & Version">
              ?
              {hasNewVersion && <span className="help-new-dot" />}
            </button>
          </div>

          <div className="header-user">
            <div className="user-dropdown" onClick={() => setShowUserDropdown(!showUserDropdown)}>
              <div className="user-info">
                <span className="user-name">{user?.name || t('admin.header.default_name')}</span>
                {selectedTurnier ? (
                  <span className="user-role user-role--turnier">🏆 {selectedTurnier.name}</span>
                ) : (
                  <span className="user-role">{user?.isAdmin ? t('admin.header.role_admin') : (user?.club_name || t('admin.header.role_club'))}</span>
                )}
                <span className="dropdown-arrow">▼</span>
              </div>

              {showUserDropdown && (
                <div className="dropdown-menu">

                  {/* ── Turnier-Auswahl ──────────────────────────── */}
                  {turniere.length > 0 && (() => {
                    const today = new Date(); today.setHours(0,0,0,0);
                    // Ein Turnier ohne Datum (Dragon Cup 2026: "Termin kommt noch")
                    // ist nicht vergangen -- es steht nur noch nicht fest.
                    // new Date(null) ist der 01.01.1970; ohne diese Unterscheidung
                    // landete es unter VERGANGENE und zeigte 1970 als Datum.
                    const ohneDatum = (tr) => !tr.datum;
                    const upcoming = turniere
                      .filter(tr => ohneDatum(tr) || new Date(tr.datum) >= today)
                      .sort((a, b) => {
                        if (ohneDatum(a) !== ohneDatum(b)) return ohneDatum(a) ? 1 : -1;  // ohne Termin ans Ende
                        if (ohneDatum(a)) return 0;
                        return new Date(a.datum) - new Date(b.datum);
                      });
                    const past = turniere
                      .filter(tr => !ohneDatum(tr) && new Date(tr.datum) < today)
                      .sort((a, b) => new Date(b.datum) - new Date(a.datum));
                    const activeId = selectedTurnier?.id || selectedTurnier?.turnier_id;
                    const fmt = (d) => new Date(d).toLocaleDateString('de-DE', { day:'2-digit', month:'short', year:'numeric' });
                    const datumZeile = (tr) => (tr.datum ? fmt(tr.datum) : (tr.datum_text || 'Termin offen'));
                    const renderRow = (tr) => {
                      const id = tr.id || tr.turnier_id;
                      return (
                        <div key={id}
                          className={`dd-turnier-item${id === activeId ? ' active' : ''}`}
                          onClick={() => { setSelectedTurnier(tr); localStorage.setItem('tda-selected-turnier-id', String(tr.turnier_id || tr.id)); setShowUserDropdown(false); }}>
                          <span className="dd-turnier-name">{tr.name}</span>
                          <span className="dd-turnier-date">{datumZeile(tr)}</span>
                          {id === activeId && <span className="dd-turnier-check">✓</span>}
                        </div>
                      );
                    };
                    return (
                      <>
                        <div className="dropdown-section">
                          <div className="dropdown-label">大会 Turniere</div>
                          <div className="dd-turnier-list">
                            {upcoming.map(renderRow)}
                            {past.length > 0 && upcoming.length > 0 && <div className="dd-turnier-divider">Vergangene</div>}
                            {past.map(renderRow)}
                          </div>
                          <div className="dropdown-item" onClick={() => { changeView('turniere'); setShowUserDropdown(false); }}>
                            <span className="dd-turnier-manage">→ Alle verwalten</span>
                          </div>
                        </div>
                        <div className="dropdown-divider" />
                      </>
                    );
                  })()}

                  {/* ── Events & Camps ───────────────────────────── */}
                  {events.length > 0 && (() => {
                    const today = new Date(); today.setHours(0,0,0,0);
                    const upcoming = events.filter(e => new Date(e.datum) >= today).sort((a,b) => new Date(a.datum)-new Date(b.datum));
                    const past     = events.filter(e => new Date(e.datum) < today).sort((a,b) => new Date(b.datum)-new Date(a.datum));
                    const fmtE = (d) => new Date(d).toLocaleDateString('de-DE', { day:'2-digit', month:'short', year:'numeric' });
                    const renderE = (e) => (
                      <div key={e.event_id} className="dd-turnier-item"
                        onClick={() => { changeView('events'); setShowUserDropdown(false); }}>
                        <span className="dd-turnier-name">{e.typ === 'Camp' ? '🏕️ ' : ''}{e.name}</span>
                        <span className="dd-turnier-date">{fmtE(e.datum)}</span>
                      </div>
                    );
                    return (
                      <>
                        <div className="dropdown-section">
                          <div className="dropdown-label">合宿 Events &amp; Camps</div>
                          <div className="dd-turnier-list">
                            {upcoming.map(renderE)}
                            {past.length > 0 && upcoming.length > 0 && <div className="dd-turnier-divider">Vergangene</div>}
                            {past.map(renderE)}
                          </div>
                          <div className="dropdown-item" onClick={() => { changeView('events'); setShowUserDropdown(false); }}>
                            <span className="dd-turnier-manage">→ Alle verwalten</span>
                          </div>
                        </div>
                        <div className="dropdown-divider" />
                      </>
                    );
                  })()}

                  <div className="dropdown-section">
                    <div className="dropdown-item" onClick={() => { changeView('overview'); setShowUserDropdown(false); }}>
                      <span>{t('admin.nav.overview')}</span>
                    </div>
                    <div className="dropdown-item" onClick={() => { navigate('/dashboard/ankuendigungen'); setShowUserDropdown(false); setUngeleseneAnnouncements(0); }}>
                      <span>{t('admin.nav.announcements')}</span>
                      {ungeleseneAnnouncements > 0 && (
                        <span style={{ marginLeft:'auto', background:'#ef4444', color:'#fff', borderRadius:'10px', padding:'0.1rem 0.45rem', fontSize:'0.7rem', fontWeight:700 }}>
                          {ungeleseneAnnouncements}
                        </span>
                      )}
                    </div>
                  </div>

                  {user?.isAdmin && (<>
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-section">
                      <div className="dropdown-label">System</div>
                      <div className="dropdown-item" onClick={() => { changeView('system-info'); setShowUserDropdown(false); }}>
                        <span>⚙️ System & Info</span>
                      </div>
                      <div className="dropdown-item" onClick={() => { changeView('audit'); setShowUserDropdown(false); }}>
                        <span>📋 Audit-Log</span>
                      </div>
                      {/* Aufgaben 171/174: Push-Nachrichten aufs Gerät */}
                      <PushSchalter />
                    </div>
                  </>)}

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-section">
                    <div className="dropdown-item" onClick={() => { setShowUserDropdown(false); navigate('/sicherheit'); }}>
                      <span>🛡️ Konto & Sicherheit</span>
                    </div>
                    <div className="dropdown-item logout" onClick={handleLogout}>
                      <span>{t('admin.nav.logout')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Zwei-Faktor fehlt: Stand 26.09.2026 hatte kein einziges Admin-Konto
          ihn eingerichtet. Ab ZWEI_FAKTOR_PFLICHT_ADMIN_AB (Backend-.env)
          ist er Pflicht — der Hinweis nennt dann das Datum. */}
      {zweiFaktor && zweiFaktor.moeglich && !zweiFaktor.aktiv && !zweiFaktor.extern && (
        <div className="md-zwei-faktor-hinweis" role="status">
          <span>
            <strong>Zwei-Faktor-Anmeldung ist für dieses Konto nicht eingerichtet.</strong>{' '}
            {zweiFaktor.pflicht_ab
              ? `Ab ${zweiFaktor.pflicht_ab.split('-').reverse().join('.')} ist sie für Admin-Konten Pflicht.`
              : 'An einem Admin-Konto hängen Turniere, Anmeldungen und Personendaten — ein Passwort allein schützt das schlecht.'}
          </span>
          <button type="button" onClick={() => navigate('/sicherheit')}>Jetzt einrichten</button>
        </div>
      )}

      {/* Main Content */}
      <React.Suspense fallback={<div className="lazy-loading-spinner">Laden…</div>}>
      <div className="dashboard-content">
        {activeView === 'overview' && (
          <DashboardOverview user={user} setActiveView={changeView} activeView={activeView} onLogout={handleLogout} selectedTurnier={selectedTurnier} turniere={turniere} setSelectedTurnier={setSelectedTurnier} openFormenWindow={openFormenWindow} setStaffTab={setStaffTab} />
        )}

        {activeView === 'wettkaempfer-registrieren' && (
          <div className="dashboard-module">
            <WettkaempferRegistrieren />
          </div>
        )}

        {activeView === 'wettkaempfer' && (
          <div className="dashboard-module">
            <WettkaempferDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'wettkaempfer-alle' && (
          <div className="dashboard-module">
            <WettkaempferDashboard selectedTurnier={null} />
          </div>
        )}

        {activeView === 'turniere' && (
          <div className="dashboard-module">
            <TurnierDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView.startsWith('turnier-neu') && (
          <div className="dashboard-module full-width">
            <TurnierAssistent />
          </div>
        )}

        {activeView.startsWith('turnier-bearbeiten') && (
          <div className="dashboard-module full-width">
            <TurnierBearbeitenPage />
          </div>
        )}

        {activeView === 'brackets' && (
          <div className="dashboard-module dashboard-module--no-bg">
            <BracketDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'formen' && (
          <div className="dashboard-module">
            <FormenDashboard turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} />
          </div>
        )}

        {activeView === 'selbstverteidigung' && (
          <div className="dashboard-module">
            <SelbstverteidigungDashboard turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} />
          </div>
        )}

        {activeView === 'matten-wettkampf' && (
          <div className="dashboard-module">
            <MattenWettkampfDashboard
              turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id}
              setActiveView={changeView}
            />
          </div>
        )}

        {activeView === 'anmeldungen' && (
          <div className="dashboard-module">
            <AnmeldeDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'einzelstarter' && (
          <div className="dashboard-module">
            <EinzelstarterDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'vereinsmails' && (
          <div className="dashboard-module">
            <VereinsmailsDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'einwaage' && (
          <div className="dashboard-module full-width">
            <EinwaageDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'checkin' && (
          <div className="dashboard-module full-width">
            <CheckinDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'modus-uebersicht' && (
          <div className="dashboard-module full-width">
            <ModusUebersichtDashboard
              initialTab={modusNavState?.initialTab}
              initialModus={modusNavState?.initialModus}
              turnierId={modusNavState?.turnierId}
              kategorieId={modusNavState?.kategorieId}
            />
          </div>
        )}

        {activeView === 'ankuendigungen' && (
          <div className="dashboard-module full-width">
            <AkuendigungenDashboard isAdmin={!!user?.isAdmin} selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'akkreditierung' && (
          <div className="dashboard-module full-width">
            <AkkreditierungDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}


        {activeView === 'druck' && (
          <div className="dashboard-module full-width">
            <DruckDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'events' && (
          <div className="dashboard-module">
            <EventsDashboard />
          </div>
        )}

        {activeView === 'streaming' && (
          <div className="dashboard-module">
            <StreamingDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'analytics' && (
          <div className="dashboard-module">
            <AnalyticsDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'divisionen' && (
          <div className="dashboard-module dashboard-module--no-bg">
            <DivisionDashboard selectedTurnier={selectedTurnier} istPlattform={!!user?.isAdmin && user?.verband_id_tenant == null} />
          </div>
        )}

        {activeView === 'formenkatalog' && (
          <div className="dashboard-module dashboard-module--no-bg">
            <FormenkatalogPage />
          </div>
        )}

        {(activeView === 'vereine' || activeView.startsWith('vereine/')) && (
          <div className="dashboard-module dashboard-module--no-bg">
            {(() => {
              const path = location.pathname;

              // Check for /dashboard/vereine/neu first (before numeric check)
              if (path === '/dashboard/vereine/neu' || activeView === 'vereine/neu') {
                return <VereinCreatePage />;
              }

              // Extract vereinId from activeView if it's a detail view
              // /dashboard/vereine/:id/bearbeiten öffnet die Vereinsdaten gleich im
              // Bearbeitungsmodus (vorher landete der Knopf nur auf der Detailseite).
              if (activeView.startsWith('vereine/')) {
                const [, vereinId, zusatz] = activeView.split('/');
                return <VereinDetailPage key={vereinId} vereinId={vereinId} bearbeiten={zusatz === 'bearbeiten'} />;
              }

              return <VereineListePage />;
            })()}
          </div>
        )}

        {activeView === 'bruchtest' && (
          <div className="dashboard-module full-width">
            <BruchtestDashboard turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} />
          </div>
        )}

        {activeView === 'zeitplan' && (
          <div className="dashboard-module full-width">
            <ZeitplanDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'ergebnisse' && (
          <div className="dashboard-module full-width">
            {/* Aufgabe 200: eine Seite für Siegerehrung, Planung und Gesamtwertung */}
            <ErgebnisDashboard selectedTurnier={selectedTurnier} startTab="gesamtwertung" setActiveView={changeView} />
          </div>
        )}

        {activeView === 'urkunden' && (
          <div className="dashboard-module full-width">
            {/* Urkunden entstehen im Druck-Bereich aus den Ergebnissen
                (utils/urkundenDruck). Vorher stand hier „in Kürze verfügbar“. */}
            <DruckDashboard selectedTurnier={selectedTurnier} startTab="ergebnisse" />
          </div>
        )}

        {activeView === 'live-ergebnisse' && (
          <div className="dashboard-module full-width">
            <LiveSpectatorDashboard turnierId={selectedTurnier?.turnier_id} />
          </div>
        )}

        {activeView === 'turnier-einstellungen' && (
          <TurnierEinstellungen />
        )}

        {(activeView === 'staff-management' || activeView === 'regstaff' || activeView === 'kampfrichter-manager') && (
          <div className="dashboard-module full-width">
            <nav className="sm-page-nav">
              {[
                { key: 'personal',     icon: '👥', label: 'Personal' },
                { key: 'reghelfer',    icon: '⚡', label: 'Registrierungshelfer' },
                { key: 'kampfrichter', icon: '🥋', label: 'Kampfrichter' },
                { key: 'einteilung',   icon: '🗂',  label: 'Einteilung' },
                { key: 'einwaage',     icon: '⚖',  label: 'Einwaage-Helfer' },
              ].map(tab => (
                <button
                  key={tab.key}
                  className={`sm-page-tab${staffTab === tab.key ? ' sm-page-tab--active' : ''}`}
                  onClick={() => setStaffTab(tab.key)}
                >
                  <span className="sm-page-tab-icon">{tab.icon}</span>
                  <span className="sm-page-tab-label">{tab.label}</span>
                </button>
              ))}
            </nav>
            {staffTab === 'personal'     && <StaffManager turnierId={selectedTurnier?.turnier_id} turnier={selectedTurnier} />}
            {staffTab === 'reghelfer'    && <RegStaffManager turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} turnier={selectedTurnier} />}
            {staffTab === 'kampfrichter' && <KampfrichterManager turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} turnier={selectedTurnier} />}
            {staffTab === 'einteilung'   && <RefereeManager turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} />}
            {staffTab === 'einwaage'     && <WeighStaffManager turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} turnier={selectedTurnier} />}
          </div>
        )}

        {activeView === 'siegerehrung' && (
          <div className="dashboard-module full-width">
            <ErgebnisDashboard selectedTurnier={selectedTurnier} setActiveView={changeView} />
          </div>
        )}

        {activeView === 'saisonrangliste' && (
          <div className="dashboard-module full-width">
            <SaisonRanglisteDashboard />
          </div>
        )}

        {BERICHT_VIEW_MAP[activeView] !== undefined && (
          <div className="dashboard-module full-width" style={{ height: '100%', overflow: 'hidden' }}>
            <BerichteDashboard selectedTurnier={selectedTurnier} initialReport={BERICHT_VIEW_MAP[activeView]} />
          </div>
        )}

        {activeView === 'kampfflaechen' && (
          <div className="dashboard-module full-width">
            <KampfflaechenManager turnierId={selectedTurnier?.turnier_id} />
          </div>
        )}

        {activeView === 'platzierung' && (
          <div className="dashboard-module full-width">
            <PlatzierungDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'sports' && user?.isAdmin && (
          <div className="dashboard-module">
            <SportsManager />
          </div>
        )}

        {activeView === 'admin' && user?.isAdmin && (
          <div className="dashboard-module">
            <AdminPanel user={user} />
          </div>
        )}

        {activeView === 'system-info' && user?.isAdmin && (
          <div className="dashboard-module">
            <SystemInfoPanel version={CURRENT_VERSION} buildDate={CURRENT_BUILD_DATE} changelog={CHANGELOG} />
          </div>
        )}

        {activeView === 'audit' && user?.isAdmin && (
          <div className="dashboard-module">
            <AuditPanel />
          </div>
        )}

        {activeView === 'verbaende' && user?.verband_id_tenant === null && (
          <div className="dashboard-module full-width">
            <VerbaendeDashboard />
          </div>
        )}

        {activeView === 'veranstalter-abrechnung' && user?.verband_id_tenant === null && (
          <div className="dashboard-module full-width">
            <VeranstalterAbrechnung />
          </div>
        )}

      </div>
      </React.Suspense>

      {/* Hilfe-Modal */}
      {showHelp && (
        <div className="help-modal-overlay" onClick={() => setShowHelp(false)}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <div className="help-modal-title">
                <span>Hilfe & Info</span>
                <span className="help-version-badge">v{CURRENT_VERSION}</span>
              </div>
              <button className="help-modal-close" onClick={() => setShowHelp(false)}>✕</button>
            </div>

            {/* Tabs */}
            <div className="help-modal-tabs">
              <button
                className={`help-tab ${helpTab === 'hilfe' ? 'active' : ''}`}
                onClick={() => setHelpTab('hilfe')}
              >Hilfe</button>
              <button
                className={`help-tab ${helpTab === 'changelog' ? 'active' : ''}`}
                onClick={() => {
                  setHelpTab('changelog');
                  localStorage.setItem(LS_VERSION_KEY, CURRENT_VERSION);
                  setHasNewVersion(false);
                }}
              >
                Changelog
                {hasNewVersion && helpTab !== 'changelog' && <span className="help-tab-dot" />}
              </button>
            </div>

            <div className="help-modal-body">
              {helpTab === 'hilfe' && (<>
                <div className="help-section">
                  <h4>Organisation</h4>
                  <div className="help-items">
                    <div className="help-item"><span className="help-icon">🏆</span><div><strong>Turnier-Einstellungen</strong><p>Name, Datum, Ort und allgemeine Turnierdaten verwalten.</p></div></div>
                    <div className="help-item"><span className="help-icon">🗂️</span><div><strong>Divisionen</strong><p>Gewichtsklassen und Altersgruppen für das Turnier konfigurieren.</p></div></div>
                    <div className="help-item"><span className="help-icon">🥋</span><div><strong>Matten</strong><p>Matten und Bereiche anlegen, Nummern und Namen vergeben.</p></div></div>
                    <div className="help-item"><span className="help-icon">👥</span><div><strong>Personal</strong><p>Kampfrichter, Helfer und weiteres Personal verwalten und Turnieren zuweisen. Einsatzplan für alle Matten.</p></div></div>
                    <div className="help-item"><span className="help-icon">📅</span><div><strong>Zeitplan</strong><p>Ablaufplan und Zeitfenster für das Turnier festlegen.</p></div></div>
                  </div>
                </div>
                <div className="help-section">
                  <h4>Wettkampf</h4>
                  <div className="help-items">
                    <div className="help-item"><span className="help-icon">📝</span><div><strong>Anmeldungen</strong><p>Vereinsanmeldungen einsehen, bestätigen oder stornieren.</p></div></div>
                    <div className="help-item"><span className="help-icon">🥊</span><div><strong>Wettkämpfer</strong><p>Alle Teilnehmer des Turniers, Filter nach Gewicht, Alter, Level.</p></div></div>
                    <div className="help-item"><span className="help-icon">🌿</span><div><strong>Formen</strong><p>Formen-Kategorien und Teilnehmer verwalten. Schreiber-Fenster starten.</p></div></div>
                    <div className="help-item"><span className="help-icon">🗺️</span><div><strong>Mattenübersicht</strong><p>Übersicht aller Matten mit zugeordneten Formen-Kategorien und Schreiber-Links.</p></div></div>
                    <div className="help-item"><span className="help-icon">🎯</span><div><strong>Brackets</strong><p>Auslosungen und K.O.-Runden erstellen und verwalten.</p></div></div>
                  </div>
                </div>
                <div className="help-section">
                  <h4>Turniertag</h4>
                  <div className="help-items">
                    <div className="help-item"><span className="help-icon">✅</span><div><strong>Check-in</strong><p>Wettkämpfer und Personal beim Turniertag einchecken.</p></div></div>
                    <div className="help-item"><span className="help-icon">⚖️</span><div><strong>Einwaage</strong><p>Gewichtskontrolle durchführen und dokumentieren.</p></div></div>
                    <div className="help-item"><span className="help-icon">🏅</span><div><strong>Ergebnisse</strong><p>Kampfergebnisse eingeben und Ranglisten berechnen.</p></div></div>
                  </div>
                </div>
                <div className="help-section">
                  <h4>Berichte & Druck</h4>
                  <div className="help-items">
                    <div className="help-item"><span className="help-icon">📊</span><div><strong>Berichte</strong><p>Statistiken, Teilnehmerlisten, Ergebnisberichte und mehr exportieren.</p></div></div>
                    <div className="help-item"><span className="help-icon">🖨️</span><div><strong>Druck</strong><p>Startlisten, Urkunden, Akkreditierungskarten und Etiketten drucken.</p></div></div>
                  </div>
                </div>
              </>)}

              {helpTab === 'changelog' && (
                <div className="changelog-list">
                  {CHANGELOG.map((entry, i) => {
                    const typeConfig = {
                      major:       { label: 'Major Update', color: '#e67e22' },
                      feature:     { label: 'Neue Funktion', color: '#2ecc71' },
                      improvement: { label: 'Verbesserung', color: '#3498db' },
                      bugfix:      { label: 'Bugfix', color: '#e74c3c' },
                      security:    { label: 'Sicherheit', color: '#9b59b6' },
                    }[entry.type] || { label: entry.type, color: '#888' };
                    return (
                      <div key={i} className={`cl-entry ${i === 0 ? 'cl-entry--latest' : ''}`}>
                        <div className="cl-entry-header">
                          <span className="cl-version">v{entry.version}</span>
                          <span className="cl-type-badge" style={{ background: typeConfig.color }}>{typeConfig.label}</span>
                          <span className="cl-date">{entry.date}</span>
                        </div>
                        <div className="cl-title">{entry.title}</div>
                        <ul className="cl-highlights">
                          {entry.highlights.map((h, j) => <li key={j}>{h}</li>)}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="help-modal-footer">
              TDA Tournament Software · v{CURRENT_VERSION} · Build {CURRENT_BUILD_DATE} · © 2024–2026 TDA International
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Admin Panel Component — Benutzerverwaltung
function AdminPanel({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // null | { type: 'create'|'edit'|'password'|'delete', user? }
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/auth/users', { headers });
      const data = await res.json();
      if (data.success) setUsers(data.data);
      else setError(data.error);
    } catch (e) {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => { setForm({ name: '', benutzername: '', email: '', passwort: '', rolle: 'verein' }); setModal({ type: 'create' }); setMsg(null); };
  const openEdit = (u) => { setForm({ name: u.name, benutzername: u.benutzername, email: u.email || '', rolle: u.rolle }); setModal({ type: 'edit', user: u }); setMsg(null); };
  const openPassword = (u) => { setForm({ passwort: '' }); setModal({ type: 'password', user: u }); setMsg(null); };
  const openDelete = (u) => { setModal({ type: 'delete', user: u }); setMsg(null); };
  const closeModal = () => { setModal(null); setMsg(null); };

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      let res, url, method, body;
      if (modal.type === 'create') {
        url = '/api/auth/users'; method = 'POST';
        body = { name: form.name, benutzername: form.benutzername, email: form.email, passwort: form.passwort, rolle: form.rolle };
      } else if (modal.type === 'edit') {
        url = `/api/auth/users/${modal.user.vereins_id}`; method = 'PUT';
        body = { name: form.name, benutzername: form.benutzername, email: form.email, rolle: form.rolle };
      } else if (modal.type === 'password') {
        url = `/api/auth/users/${modal.user.vereins_id}/password`; method = 'PUT';
        body = { passwort: form.passwort };
      } else if (modal.type === 'delete') {
        url = `/api/auth/users/${modal.user.vereins_id}`; method = 'DELETE'; body = {};
      }
      res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) {
        setMsg({ ok: true, text: data.message });
        await loadUsers();
        if (modal.type !== 'edit' && modal.type !== 'password') setTimeout(closeModal, 800);
      } else {
        setMsg({ ok: false, text: data.error });
      }
    } catch (e) {
      setMsg({ ok: false, text: 'Verbindungsfehler' });
    } finally {
      setSaving(false);
    }
  };

  const rolleLabel = (r) => ({ admin: 'Admin', verein: 'Verein', kampfrichter: 'Kampfrichter' }[r] || r);
  const rolleBadge = (r) => ({ admin: '#e74c3c', verein: '#3498db', kampfrichter: '#27ae60' }[r] || '#888');

  return (
    <div className="admin-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>👥 Benutzerverwaltung</h2>
        <button className="btn-primary" onClick={openCreate} style={{ padding: '8px 18px' }}>+ Benutzer anlegen</button>
      </div>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Lade Benutzer…</p>}
      {error && <p style={{ color: '#e74c3c' }}>{error}</p>}

      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
              {['Name', 'Benutzername', 'E-Mail', 'Rolle', 'Letzter Login', 'Aktionen'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.vereins_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '10px' }}>{u.name}</td>
                <td style={{ padding: '10px', fontFamily: 'monospace' }}>{u.benutzername}</td>
                <td style={{ padding: '10px', color: 'var(--text-muted)', fontSize: 12 }}>{u.email}</td>
                <td style={{ padding: '10px' }}>
                  <span style={{ background: rolleBadge(u.rolle), color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 11 }}>
                    {rolleLabel(u.rolle)}
                  </span>
                </td>
                <td style={{ padding: '10px', color: 'var(--text-muted)', fontSize: 12 }}>
                  {u.letzter_login ? new Date(u.letzter_login).toLocaleDateString('de-DE') : '—'}
                </td>
                <td style={{ padding: '10px' }}>
                  <button onClick={() => openEdit(u)} style={{ marginRight: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Bearbeiten</button>
                  <button onClick={() => openPassword(u)} style={{ marginRight: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Passwort</button>
                  {u.vereins_id !== user?.vereins_id && (
                    <button onClick={() => openDelete(u)} style={{ padding: '4px 10px', fontSize: 12, cursor: 'pointer', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4 }}>Löschen</button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)', textAlign: 'center' }}>Keine Benutzer vorhanden</td></tr>
            )}
          </tbody>
        </table>
      )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}>
          <div style={{ background: 'rgba(22,22,34,0.99)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 28, minWidth: 380, maxWidth: 480, width: '100%' }}>
            <h3 style={{ marginTop: 0 }}>
              {modal.type === 'create' && 'Neuen Benutzer anlegen'}
              {modal.type === 'edit' && `Benutzer bearbeiten: ${modal.user.name}`}
              {modal.type === 'password' && `Passwort ändern: ${modal.user.name}`}
              {modal.type === 'delete' && `Benutzer löschen?`}
            </h3>

            {modal.type === 'delete' ? (
              <p>Soll <strong>{modal.user.name}</strong> ({modal.user.benutzername}) wirklich gelöscht werden?</p>
            ) : modal.type === 'password' ? (
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Neues Passwort</label>
                <input type="password" value={form.passwort} onChange={e => setForm(f => ({ ...f, passwort: e.target.value }))}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }} autoFocus />
                {/* Eine Passwortregel für alle Konten (25.10.2026); der Server prüft dieselbe */}
                <div className="pwr-dashboard"><PasswortRegeln passwort={form.passwort} /></div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Name', key: 'name', type: 'text' },
                  { label: 'Benutzername', key: 'benutzername', type: 'text' },
                  { label: 'E-Mail (optional)', key: 'email', type: 'email' },
                  ...(modal.type === 'create' ? [{ label: 'Passwort', key: 'passwort', type: 'password' }] : []),
                ].map(({ label, key, type }) => (
                  <div key={key}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>{label}</label>
                    <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
                    {key === 'passwort' && <div className="pwr-dashboard"><PasswortRegeln passwort={form.passwort} /></div>}
                  </div>
                ))}
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Rolle</label>
                  <select value={form.rolle || 'verein'} onChange={e => setForm(f => ({ ...f, rolle: e.target.value }))}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
                    <option value="admin">Admin</option>
                    <option value="verein">Verein</option>
                    <option value="kampfrichter">Kampfrichter</option>
                  </select>
                </div>
              </div>
            )}

            {msg && (
              <p style={{ color: msg.ok ? '#27ae60' : '#e74c3c', marginTop: 12, fontSize: 13 }}>{msg.text}</p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={closeModal} style={{ padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>Abbrechen</button>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '8px 18px', borderRadius: 6, background: modal.type === 'delete' ? '#e74c3c' : 'var(--accent-primary)', color: '#fff', border: 'none', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Speichere…' : modal.type === 'delete' ? 'Ja, löschen' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Dashboard Overview Component
function DashboardOverview({ user, setActiveView, activeView, onLogout, selectedTurnier, turniere, setSelectedTurnier, openFormenWindow, setStaffTab }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modulesTab, setModulesTab] = useState(
    () => localStorage.getItem('tda-dashboard-tab') || 'organisation'
  );
  const [showSchnellreg, setShowSchnellreg] = useState(false);
  const [stats, setStats] = useState({
    wettkaempfer: 0,
    anmeldungen: 0,
    personal: 0,
    kategorien: 0
  });
  const [, setRecentActivity] = useState([]);
  const [briefing, setBriefing] = useState(null); // null = nicht geladen, [] = keine Punkte, [...] = Punkte
  const [showBriefing, setShowBriefing] = useState(false);

  // Primitive ID als stabile Dep-Referenz (verhindert Object-Ref-Wechsel-Trigger)
  const selectedTurnierId = selectedTurnier?.turnier_id ?? selectedTurnier?.id ?? null;

  const fetchDashboardStats = useCallback(async () => {
    if (!selectedTurnierId) {
      setStats({ wettkaempfer: 0, anmeldungen: 0, personal: 0, activeStreams: 0 });
      setRecentActivity([]);
      return;
    }

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    const briefingKey = `tda-briefing-${new Date().toISOString().slice(0,10)}`;

    // Alle Requests parallel starten
    const [wkRes, anmRes, persRes, actRes, briefRes, divRes] = await Promise.allSettled([
      fetch(`/api/wettkaempfer?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/anmeldungen/stats?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/personal/turnier/${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/dashboard/recent-activity?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/dashboard/briefing?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/divisionen?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
    ]);

    // Wettkämpfer
    if (wkRes.status === 'fulfilled' && wkRes.value) {
      const d = wkRes.value;
      const count = d.success && d.data ? d.data.length : Array.isArray(d) ? d.length : 0;
      setStats(prev => ({ ...prev, wettkaempfer: count }));
    }

    // Anmeldungen
    if (anmRes.status === 'fulfilled' && anmRes.value) {
      setStats(prev => ({ ...prev, anmeldungen: anmRes.value.data?.gesamt?.gesamt_anmeldungen || 0 }));
    }

    // Personal
    if (persRes.status === 'fulfilled' && persRes.value) {
      setStats(prev => ({ ...prev, personal: persRes.value.data?.length || 0 }));
    }

    // Letzte Aktivitäten
    if (actRes.status === 'fulfilled' && actRes.value) {
      setRecentActivity(actRes.value.data || []);
    }

    // Kategorien/Divisionen
    if (divRes.status === 'fulfilled' && divRes.value) {
      const d = divRes.value;
      const count = d.success && d.data ? d.data.length : Array.isArray(d) ? d.length : 0;
      setStats(prev => ({ ...prev, kategorien: count }));
    }

    // Tagesbriefing
    if (briefRes.status === 'fulfilled' && briefRes.value) {
      const briefData = briefRes.value.data || [];
      setBriefing(briefData);
      if (!localStorage.getItem(briefingKey)) {
        if (briefData.some(i => !i.done)) setShowBriefing(true);
      }
    }
  }, [selectedTurnierId]); // primitive dep → kein Re-Trigger bei gleicher turnier_id mit neuer Objekt-Referenz

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // „Aktive Turniere“ (19.10.2026): stand fest auf 1, sobald ein Turnier
  // gewählt war. Jetzt gezählt aus der Kopf-Liste (GET /api/turniere: eigener
  // Verband, ohne archivierte), wirksamer Status Anmeldung offen,
  // Anmeldung geschlossen oder Läuft.
  // Übungsturniere (turniere.uebung = 1, backend/utils/uebung.js) zählen
  // nicht mit: sie sind kein echtes Turnier.
  const aktiveTurniere = turniere.filter((x) => Number(x.uebung) !== 1 && [
    TURNIER_STATUS.AKTIV, TURNIER_STATUS.ANMELDUNG_GESCHLOSSEN, TURNIER_STATUS.LAUFEND,
  ].includes(x.status_wirksam || x.status)).length;

  // ── Kacheln (18.10.2026) ───────────────────────────────────────────────
  // Eine Liste statt fünf handgeschriebener Raster: jede Kachel steht genau
  // einmal da (vorher Modus-Übersicht 3x, Personal 2x), und die Schnellsuche
  // durchsucht dieselbe Liste. `zeichen` ist das Kanji, `bedeutung` steht
  // klein darunter, damit niemand raten muss, was es heißt.
  const [suche, setSuche] = useState('');
  const sucheRef = React.useRef(null);
  const turnierIdFuerFenster = selectedTurnier?.turnier_id || selectedTurnier?.id;
  const fensterOeffnen = (pfad, masse) => {
    if (!turnierIdFuerFenster) { toast.info(t('admin.common.select_tournament_first')); return; }
    window.open(`${pfad}/${turnierIdFuerFenster}`, '_blank', masse);
  };
  const zuPersonal = (reiter) => { setStaffTab(reiter); setActiveView('staff-management'); };

  const MODULE = [
    // Organisation
    { id: 'turniere', phase: 'organisation', zeichen: '大会', bedeutung: 'Turnier', titel: t('admin.modules.tournaments'), text: t('admin.modules.tournaments_sub'), onClick: () => setActiveView('turniere') },
    { id: 'vereine', phase: 'organisation', zeichen: '道場', bedeutung: 'Verein', titel: t('admin.modules.clubs'), text: t('admin.modules.clubs_sub'), onClick: () => setActiveView('vereine') },
    { id: 'divisionen', phase: 'organisation', zeichen: '部門', bedeutung: 'Klasse', titel: t('admin.modules.divisions'), text: t('admin.modules.divisions_sub'), hinweis: 'Division = eine ausgeschriebene Startklasse, z. B. „Pointfighting Herren −75 kg, Fortgeschrittene“. Wer sich anmeldet, wählt eine Division.', onClick: () => setActiveView('divisionen') },
    { id: 'formenkatalog', phase: 'organisation', nurAdmin: true, zeichen: '型録', bedeutung: 'Katalog', titel: 'Formenkatalog', text: 'Kata, Poomsae, Tul und Hyong pflegen: Quellen, Schreibweisen, wer sie gewählt hat', hinweis: 'Liste der Formen, aus der am Turniertag gewählt wird.', worte: 'kata poomsae tul hyong formen katalog doppelt unsicher', onClick: () => setActiveView('formenkatalog') },
    { id: 'kampfflaechen', phase: 'organisation', zeichen: '畳', bedeutung: 'Matte', titel: t('admin.modules.mats'), text: t('admin.modules.mats_sub'), hinweis: 'Matte bzw. Ring in der Halle. Jede Division wird einer Matte zugeteilt.', worte: 'kampfflächen tatami ring', onClick: () => setActiveView('kampfflaechen') },
    { id: 'turnier-einstellungen', phase: 'organisation', zeichen: '設定', bedeutung: 'Einstellung', titel: t('admin.modules.tournament_settings'), text: t('admin.modules.tournament_settings_sub'), onClick: () => setActiveView('turnier-einstellungen') },
    { id: 'staff-management', phase: 'organisation', zeichen: '役員', bedeutung: 'Personal', titel: t('admin.modules.staff'), text: 'Personal, Registrierungshelfer, Kampfrichter, Einteilung und Einwaage-Helfer', worte: 'staff helfer kampfrichter registrierungshelfer einteilung tagespasswort', onClick: () => zuPersonal('personal') },
    { id: 'ankuendigungen', phase: 'organisation', zeichen: '知らせ', bedeutung: 'Nachricht', titel: 'Ankündigungen', text: 'Infos & Neuigkeiten für Teilnehmer', onClick: () => navigate('/dashboard/ankuendigungen') },
    { id: 'modus-uebersicht', phase: 'organisation', zeichen: '方式', bedeutung: 'Modus', titel: 'Modus-Übersicht', text: 'Wertungsart und Anzeige je Disziplin: Farben, Beschriftungen, Leinwand', hinweis: 'Modus = Wertungsart einer Disziplin (K.o.-Bracket, Pool jeder gegen jeden, Formen-Wertung …).', worte: 'display einstellungen anzeige leinwand pointfighting kumite grappling', onClick: () => setActiveView('modus-uebersicht') },
    { id: 'events', phase: 'organisation', zeichen: '合宿', bedeutung: 'Lehrgang', titel: 'Lehrgänge & Camps', text: 'Seminare, Prüfungen, Trainingscamps & Visa-Briefe', onClick: () => setActiveView('events') },
    { id: 'sports', phase: 'organisation', nurAdmin: true, zeichen: '種目', bedeutung: 'Sportart', titel: t('admin.modules.multi_sport'), text: t('admin.modules.multi_sport_sub'), onClick: () => setActiveView('sports') },
    { id: 'admin', phase: 'organisation', nurAdmin: true, zeichen: '管理', bedeutung: 'Verwaltung', titel: t('admin.modules.administration'), text: t('admin.modules.administration_sub'), worte: 'benutzer', onClick: () => setActiveView('admin') },

    // Wettkampftag
    { id: 'schnellreg', phase: 'wettkampftag', hervor: true, zeichen: '速', bedeutung: 'Schnell', titel: 'Schnellregistrierung', text: 'Express · Kategorie-zuerst · Vereinsliste · QR Self-Service', worte: 'nachmeldung', onClick: () => setShowSchnellreg(true) },
    { id: 'wettkaempfer-registrieren', phase: 'wettkampftag', zeichen: '登録', bedeutung: 'Eintragen', titel: t('admin.modules.register_athletes'), text: t('admin.modules.register_athletes_sub'), onClick: () => setActiveView('wettkaempfer-registrieren') },
    { id: 'wettkaempfer', phase: 'wettkampftag', zeichen: '選手', bedeutung: 'Kämpfer', titel: t('admin.modules.athletes'), text: t('admin.modules.athletes_sub'), worte: 'athleten teilnehmer', onClick: () => setActiveView('wettkaempfer') },
    { id: 'wettkaempfer-alle', phase: 'wettkampftag', zeichen: '全員', bedeutung: 'Alle', titel: 'Alle Wettkämpfer', text: 'Turnierübergreifend: alle Athleten des Verbands', hinweis: 'Alle Wettkämpfer des Verbands, unabhängig vom gewählten Turnier', onClick: () => setActiveView('wettkaempfer-alle') },
    { id: 'anmeldungen', phase: 'wettkampftag', zeichen: '申込', bedeutung: 'Meldung', titel: t('admin.modules.registrations'), text: t('admin.modules.registrations_sub'), onClick: () => setActiveView('anmeldungen') },
    { id: 'einzelstarter', phase: 'wettkampftag', zeichen: '単独', bedeutung: 'Allein', titel: 'Einzelstarter & kleine Klassen', text: 'Wer steht allein? Umlegen, Verein informieren, kleine Klassen zusammenlegen', hinweis: 'Einzelstarter = jemand, der in seiner Division keinen Gegner hat. Hier wird entschieden, wohin er wechselt.', onClick: () => setActiveView('einzelstarter') },
    { id: 'vereinsmails', phase: 'wettkampftag', zeichen: '連絡', bedeutung: 'Post', titel: 'Mails an Vereine', text: 'Anmeldebestätigung und Turnierinfo mit Matte und Uhrzeit', worte: 'e-mail', onClick: () => setActiveView('vereinsmails') },
    { id: 'checkin', phase: 'wettkampftag', zeichen: '受付', bedeutung: 'Empfang', titel: 'Check-in', text: 'Wer ist da? Anwesenheit & Zahlung vor Ort', worte: 'anwesenheit', onClick: () => setActiveView('checkin') },
    { id: 'einwaage', phase: 'wettkampftag', zeichen: '計量', bedeutung: 'Wiegen', titel: 'Einwaage', text: 'Gewicht am Turniertag kontrollieren', hinweis: 'Einwaage = Wiegen am Turniertag. Wer nicht in seine Gewichtsklasse passt, wechselt die Division.', worte: 'gewicht waage', onClick: () => setActiveView('einwaage') },
    { id: 'akkreditierung', phase: 'wettkampftag', zeichen: '認証', bedeutung: 'Ausweis', titel: 'Akkreditierung', text: 'Ausweise & Startberechtigung', onClick: () => setActiveView('akkreditierung') },
    { id: 'brackets', phase: 'wettkampftag', zeichen: '組合', bedeutung: 'Paarung', titel: t('admin.modules.brackets'), text: t('admin.modules.brackets_sub'), hinweis: 'Bracket = der Turnierbaum einer Division: wer gegen wen kämpft und wer weiterkommt (K.o.-System).', worte: 'turnierbaum auslosung pool', onClick: () => setActiveView('brackets') },
    { id: 'zeitplan', phase: 'wettkampftag', zeichen: '予定', bedeutung: 'Plan', titel: t('admin.modules.schedule'), text: t('admin.modules.schedule_sub'), hinweis: 'Zeitplan = wann welche Division auf welcher Matte dran ist.', worte: 'ablauf uhrzeit', onClick: () => setActiveView('zeitplan') },
    { id: 'turniertag', phase: 'wettkampftag', hervor: true, zeichen: '準備', bedeutung: 'Bereit', titel: 'Turniertag vorbereiten', text: 'Gemessene Prüfung, Automatik nach der Einwaage, Gerätekarte mit QR-Codes, Start', worte: 'checkliste wettkampftag start qr geräte', onClick: () => { if (!turnierIdFuerFenster) { toast.info(t('admin.common.select_tournament_first')); return; } navigate(`/turniertag/${turnierIdFuerFenster}`); } },
    { id: 'leitstelle', phase: 'wettkampftag', hervor: true, neuesFenster: true, zeichen: '司令', bedeutung: 'Leitung', titel: 'Leitstelle', text: 'Alle Matten auf einen Blick: Kategorie, Verzug, Tablets, Fehler', hinweis: 'Gesamtsicht für die Turnierleitung, aktualisiert sich alle 5 Sekunden.', worte: 'übersicht ampel turnierleitung matten', onClick: () => fensterOeffnen('/leitstelle', undefined) },
    { id: 'aufruf-steuerung', phase: 'wettkampftag', neuesFenster: true, zeichen: '指揮', bedeutung: 'Steuerung', titel: 'Aufruf-Steuerung', text: 'Kategorien je Matte aufrufen, steuert den Hallenschirm', worte: 'schreiber aufruf control', onClick: () => fensterOeffnen('/aufruf-control', 'width=600,height=900,menubar=no,toolbar=no,location=no,status=no') },
    { id: 'aufruf-display', phase: 'wettkampftag', neuesFenster: true, zeichen: '呼', bedeutung: 'Aufruf', titel: 'Aufruf-Hallenschirm', text: 'Jetzt und als Nächstes je Matte, für Beamer oder Zweitmonitor', worte: 'display leinwand beamer', onClick: () => fensterOeffnen('/aufruf', 'width=1600,height=960,menubar=no,toolbar=no,location=no,status=no') },
    { id: 'ansage', phase: 'wettkampftag', neuesFenster: true, zeichen: '放送', bedeutung: 'Durchsage', titel: 'Ansage-Station', text: 'Spricht die Aufrufe über die Lautsprecheranlage', worte: 'lautsprecher sprache', onClick: () => fensterOeffnen('/ansage', undefined) },
    { id: 'druck', phase: 'wettkampftag', zeichen: '印刷', bedeutung: 'Druck', titel: 'Druck', text: 'Startlisten, Meldebogen, Ergebnisse & Urkunden', worte: 'drucken startliste', onClick: () => setActiveView('druck') },

    // Wettkampf
    { id: 'formen', phase: 'wettkampf', zeichen: '型', bedeutung: 'Form', titel: t('admin.modules.forms'), text: t('admin.modules.forms_sub'), hinweis: 'Formen (Kata) = vorgeführte Technikfolgen, von Kampfrichtern mit Noten bewertet. Hier werden Kategorien und Startreihenfolge verwaltet.', worte: 'kata', onClick: () => setActiveView('formen') },
    { id: 'selbstverteidigung', phase: 'wettkampf', zeichen: '護身', bedeutung: 'Selbstschutz', titel: 'Selbstverteidigung', text: 'SV-Kategorien & Wertung', onClick: () => setActiveView('selbstverteidigung') },
    { id: 'matten-wettkampf', phase: 'wettkampf', zeichen: '試合場', bedeutung: 'Kampfplatz', titel: 'Mattenübersicht', text: 'Alle Matten mit Kategorien und Schreiber-Links', worte: 'mattenfläche schreiber', onClick: () => setActiveView('matten-wettkampf') },
    { id: 'bruchtest', phase: 'wettkampf', zeichen: '試割', bedeutung: 'Bruchtest', titel: t('admin.modules.breaking'), text: t('admin.modules.breaking_sub'), onClick: () => setActiveView('bruchtest') },

    // Ergebnisse
    { id: 'platzierung', phase: 'ergebnisse', zeichen: '順位', bedeutung: 'Rang', titel: t('admin.modules.find_placement'), text: 'Platzierungen abrufen & prüfen', hinweis: 'Platzierung = die Plätze 1–3 je Division, aus Brackets und Formen-Wertung.', onClick: () => setActiveView('platzierung') },
    { id: 'siegerehrung', phase: 'ergebnisse', zeichen: '表彰', bedeutung: 'Ehrung', titel: 'Siegerehrung & Gesamtwertung', text: 'Podium-Anzeige, Planung der Ehrungen und Punkte je Athlet', worte: 'ergebnisse podium gesamtwertung', onClick: () => setActiveView('siegerehrung') },
    { id: 'live-ergebnisse', phase: 'ergebnisse', zeichen: '速報', bedeutung: 'Live', titel: t('admin.modules.live_results'), text: t('admin.modules.live_results_sub'), onClick: () => setActiveView('live-ergebnisse') },
    { id: 'urkunden', phase: 'ergebnisse', zeichen: '賞状', bedeutung: 'Urkunde', titel: t('admin.modules.certificates'), text: 'Urkunden aus den Ergebnissen drucken', worte: 'zertifikat', onClick: () => setActiveView('urkunden') },
    { id: 'saisonrangliste', phase: 'ergebnisse', zeichen: '年間', bedeutung: 'Jahr', titel: t('admin.modules.season_ranking'), text: t('admin.modules.season_ranking_sub'), worte: 'ranking', onClick: () => setActiveView('saisonrangliste') },
    { id: 'analytics', phase: 'ergebnisse', zeichen: '統計', bedeutung: 'Statistik', titel: t('admin.modules.statistics'), text: t('admin.modules.statistics_sub'), onClick: () => setActiveView('analytics') },

    // Berichte (der Reiter war bis 18.10.2026 leer)
    { id: 'berichte', phase: 'berichte', zeichen: '報告', bedeutung: 'Bericht', titel: 'Berichte', text: 'Teilnehmer, Vereine, Finanzen, Medaillen, Kampfrichter und mehr', worte: 'auswertung export finanzen medaillen', onClick: () => setActiveView('berichte') },
    { id: 'druck-berichte', phase: 'berichte', zeichen: '印刷', bedeutung: 'Druck', titel: 'Druck', text: 'Startlisten, Meldebogen, Ergebnisse & Urkunden', onClick: () => setActiveView('druck'), nichtSuchen: true },
  ];

  const PHASEN = [
    { id: 'organisation', label: 'Organisation' },
    { id: 'wettkampftag', label: 'Wettkampftag' },
    { id: 'wettkampf', label: 'Wettkampf' },
    { id: 'ergebnisse', label: 'Ergebnisse' },
    { id: 'berichte', label: 'Berichte' },
  ];
  const phaseLabel = (id) => (PHASEN.find(p => p.id === id) || {}).label || '';

  const sichtbareModule = MODULE.filter(m => !m.nurAdmin || user?.isAdmin);
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const suchTreffer = (() => {
    const q = norm(suche).trim();
    if (!q) return [];
    const teile = q.split(/\s+/);
    return sichtbareModule
      .filter(m => !m.nichtSuchen)
      .filter(m => {
        const heu = norm([m.titel, m.text, m.bedeutung, m.worte, phaseLabel(m.phase)].join(' '));
        return teile.every(teil => heu.includes(teil));
      })
      // Treffer im Titel zuerst
      .sort((a, b) => (norm(b.titel).includes(teile[0]) ? 1 : 0) - (norm(a.titel).includes(teile[0]) ? 1 : 0));
  })();

  // Taste / setzt den Cursor in die Suche (nicht, wenn gerade getippt wird)
  useEffect(() => {
    const beiTaste = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return;
      e.preventDefault();
      if (sucheRef.current) sucheRef.current.focus();
    };
    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, []);

  const renderModul = (m, mitPhase) => (
    <div
      key={m.id}
      className={`module-card${m.nurAdmin ? ' admin' : ''}${m.hervor ? ' module-card--highlight' : ''}`}
      onClick={m.onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); m.onClick(); } }}
      role="button"
      tabIndex={0}
      title={m.hinweis}
    >
      <div className="module-zeichen" aria-hidden="true">
        <div className="module-icon">{m.zeichen}</div>
        <span className="module-zeichen-text">{m.bedeutung}</span>
      </div>
      <div className="module-content">
        <h4>{m.titel}</h4>
        <p>{m.text}</p>
        {mitPhase && <span className="module-phase">{phaseLabel(m.phase)}</span>}
      </div>
      <div className="module-arrow" aria-hidden="true">{m.neuesFenster ? '↗' : '→'}</div>
    </div>
  );

  return (
    <div className="dashboard-overview">
      {/* Kombinierter Container: Statistik-Karten */}
      {turniere.length > 0 && (
        <div className="turnier-stats-combined">
          {/* Kompakte Statistik-Karten */}
          <div className="stats-grid-compact">
            <div key="stat-turniere" className="stat-card-compact">
              <div className="stat-icon-compact">🏆</div>
              <div className="stat-content-compact">
                <h3>{aktiveTurniere}</h3>
                <p>{t('admin.overview.stat_tournaments')}</p>
              </div>
            </div>

            <div key="stat-wettkaempfer" className="stat-card-compact">
              <div className="stat-icon-compact">🥊</div>
              <div className="stat-content-compact">
                <h3>{stats.wettkaempfer}</h3>
                <p>{t('admin.overview.stat_athletes')}</p>
              </div>
            </div>

            <div key="stat-anmeldungen" className="stat-card-compact">
              <div className="stat-icon-compact">📝</div>
              <div className="stat-content-compact">
                <h3>{stats.anmeldungen}</h3>
                <p>{t('admin.overview.stat_registrations')}</p>
              </div>
            </div>

            <div key="stat-personal" className="stat-card-compact">
              <div className="stat-icon-compact">👥</div>
              <div className="stat-content-compact">
                <h3>{stats.personal}</h3>
                <p>Personal</p>
              </div>
            </div>

            <div key="stat-kategorien" className="stat-card-compact">
              <div className="stat-icon-compact">🗂️</div>
              <div className="stat-content-compact">
                <h3>{stats.kategorien}</h3>
                <p>Kategorien</p>
              </div>
            </div>

            {/* Countdown zum nächsten Turnier */}
            {(() => {
              const today = new Date(); today.setHours(0,0,0,0);
              const next = turniere
                .map(t => ({ ...t, d: new Date(t.datum) }))
                .filter(t => t.d >= today)
                .sort((a, b) => a.d - b.d)[0];
              if (!next) return null;
              const days = Math.round((next.d - today) / 86400000);
              return (
                <div key="stat-countdown" className="stat-card-compact stat-card-countdown">
                  <div className="stat-icon-compact">📅</div>
                  <div className="stat-content-compact">
                    <h3>{days === 0 ? 'Heute!' : `${days} Tage`}</h3>
                    <p className="stat-countdown-name" title={next.name}>{next.name}</p>
                  </div>
                </div>
              );
            })()}

            {/* Wettkampftag Button in Stats-Grid */}
            {/* Turniertag vorbereiten (18.10.2026): gemessen auf dem Server statt
                Haken im Browser (vorher WettkampftagChecklist). */}
            <div key="stat-wettkampftag" className="stat-card-compact stat-card-wettkampftag" role="button" tabIndex={0}
              onClick={() => { if (!turnierIdFuerFenster) { toast.info(t('admin.common.select_tournament_first')); return; } navigate(`/turniertag/${turnierIdFuerFenster}`); }}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && turnierIdFuerFenster) { e.preventDefault(); navigate(`/turniertag/${turnierIdFuerFenster}`); } }}>
              <div className="stat-icon-compact">🏅</div>
              <div className="stat-content-compact">
                <h3>Wettkampftag</h3>
                <p>Vorbereitung</p>
              </div>
            </div>
          </div>
        </div>
      )}


      {showSchnellreg && (
        <React.Suspense fallback={<div className="lazy-loading-spinner">Laden…</div>}>
          <SchnellregistrierungModal
            turnier={selectedTurnier}
            onClose={() => setShowSchnellreg(false)}
          />
        </React.Suspense>
      )}

      {/* Aktives Turnier Banner */}
      {selectedTurnier && (() => {
        const today = new Date(); today.setHours(0,0,0,0);
        const datum = new Date(selectedTurnier.datum); datum.setHours(0,0,0,0);
        const diff = Math.round((datum - today) / 86400000);
        // Phase = echter Turnierstatus aus lib/turnierStatus (19.10.2026).
        // Früher aus dem Datum geraten: „Abgeschlossen“, sobald das Datum
        // vorbei war, auch wenn niemand abgeschlossen hatte.
        const wirksam = selectedTurnier.status_wirksam || selectedTurnier.status;
        const phase = statusText(wirksam);
        const phaseColor = {
          [TURNIER_STATUS.AKTIV]: '#4ade80',
          [TURNIER_STATUS.ANMELDUNG_GESCHLOSSEN]: '#fbbf24',
          [TURNIER_STATUS.LAUFEND]: '#ff4444',
          [TURNIER_STATUS.ABGESCHLOSSEN]: '#666',
          [TURNIER_STATUS.ABGESAGT]: '#666',
        }[wirksam] || '#d4af37';
        const datumStr = selectedTurnier.datum
          ? new Date(selectedTurnier.datum).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
          : (selectedTurnier.datum_text || 'Termin offen');
        return (
          <div
            className="active-turnier-banner active-turnier-banner--clickable"
            onClick={() => { setModulesTab('wettkampftag'); localStorage.setItem('tda-dashboard-tab', 'wettkampftag'); }}
            title="Zu Wettkampftag"
          >
            <div className="active-turnier-banner__phase" style={{ color: phaseColor }}>
              <span className="active-turnier-banner__phase-dot" style={{ background: phaseColor }} />
              {phase}
            </div>
            <div className="active-turnier-banner__name">{selectedTurnier.name}</div>
            <div className="active-turnier-banner__meta">
              <span>📅 {datumStr}</span>
              {selectedTurnier.ort && <span>📍 {selectedTurnier.ort}</span>}
              {diff > 0 && <span>⏳ noch {diff} Tag{diff !== 1 ? 'e' : ''}</span>}
              {diff === 0 && <span style={{ color: '#ff4444', fontWeight: 600 }}>🏆 Heute!</span>}
            </div>
          </div>
        );
      })()}

      {/* Dashboard Modules */}
      <div className="dashboard-modules">
        <div className="modules-container">
          {/* Schnellsuche: findet jede Kachel, egal unter welcher Phase sie
              liegt. Taste / setzt den Cursor hinein, Esc leert. */}
          <div className="md-suche">
            <input
              ref={sucheRef}
              type="search"
              className="md-suche-feld"
              placeholder="Modul suchen, z. B. Einwaage (Taste /)"
              aria-label="Modul suchen"
              value={suche}
              onChange={e => setSuche(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') { setSuche(''); e.currentTarget.blur(); }
                if (e.key === 'Enter' && suchTreffer.length > 0) { suchTreffer[0].onClick(); }
              }}
            />
            {suche && (
              <button type="button" className="md-suche-leeren" onClick={() => setSuche('')} aria-label="Suche leeren">✕</button>
            )}
          </div>

          {/* Tabs Navigation */}
          <div className="dashboard-tabs">
            {PHASEN.map(ph => (
              <button
                key={ph.id}
                className={`dashboard-tab dashboard-tab--img ${!suche && modulesTab === ph.id ? 'active' : ''}`}
                onClick={() => { setSuche(''); setModulesTab(ph.id); localStorage.setItem('tda-dashboard-tab', ph.id); }}
              >
                <img src={`${process.env.PUBLIC_URL}/tab-${ph.id}.webp?v=4`} alt="" />
                <div className="tab-img-label"><span>{ph.label}</span></div>
              </button>
            ))}
          </div>

          {suche ? (
            suchTreffer.length > 0 ? (
              <div className="modules-grid">
                {suchTreffer.map(m => renderModul(m, true))}
              </div>
            ) : (
              <p className="md-suche-leer">Kein Modul passt zu „{suche}“.</p>
            )
          ) : (
            <div className="modules-grid">
              {sichtbareModule.filter(m => m.phase === modulesTab).map(m => renderModul(m, false))}
            </div>
          )}

        </div>

      </div>

      {/* SAS Lizenzen & Verbände → jetzt unter /admin */}

      {/* Tagesbriefing-Modal */}
      {showBriefing && briefing && (
        <div className="briefing-overlay" onClick={() => {
          localStorage.setItem(`tda-briefing-${new Date().toISOString().slice(0,10)}`, '1');
          setShowBriefing(false);
        }}>
          <div className="briefing-modal" onClick={e => e.stopPropagation()}>
            <div className="briefing-header">
              <div className="briefing-title">
                <span className="briefing-icon">📋</span>
                <div>
                  <h3>{(() => { const h = new Date().getHours(); return h < 12 ? 'Guten Morgen!' : h < 18 ? 'Guten Tag!' : 'Guten Abend!'; })()}</h3>
                  <p>Offene Punkte für das nächste Turnier</p>
                </div>
              </div>
              <button className="briefing-close" onClick={() => {
                localStorage.setItem(`tda-briefing-${new Date().toISOString().slice(0,10)}`, '1');
                setShowBriefing(false);
              }}>✕</button>
            </div>
            <div className="briefing-body">
              {briefing.map(item => (
                <div
                  key={item.key}
                  className={`briefing-item ${item.done ? 'done' : 'open'}`}
                  onClick={() => {
                    if (item.done) return;
                    localStorage.setItem(`tda-briefing-${new Date().toISOString().slice(0,10)}`, '1');
                    setShowBriefing(false);
                    setActiveView(item.link);
                  }}
                >
                  <span className="briefing-check">{item.done ? '✅' : '⬜'}</span>
                  <span className="briefing-label">{item.label}</span>
                  {item.count > 0 && (
                    <span className={`briefing-badge ${item.invertCount ? 'badge-warn' : 'badge-ok'}`}>
                      {item.invertCount ? `${item.count} offen` : item.count}
                    </span>
                  )}
                  {!item.done && <span className="briefing-arrow">→</span>}
                </div>
              ))}
            </div>
            <div className="briefing-footer">
              <button className="briefing-dismiss" onClick={() => {
                localStorage.setItem(`tda-briefing-${new Date().toISOString().slice(0,10)}`, '1');
                setShowBriefing(false);
              }}>Schließen — heute nicht mehr anzeigen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Modernes Turnier-Auswahlfeld

// ── System & Info Panel ─────────────────────────────────
function SystemInfoPanel({ version, buildDate, changelog }) {
  const typeConfig = {
    major:       { label: 'Major Update', color: '#e67e22' },
    feature:     { label: 'Neue Funktion', color: '#2ecc71' },
    improvement: { label: 'Verbesserung', color: '#3498db' },
    bugfix:      { label: 'Bugfix', color: '#e74c3c' },
    security:    { label: 'Sicherheit', color: '#9b59b6' },
  };
  return (
    <div className="system-info-panel">
      <div className="system-info-header">
        <h2>⚙️ System & Info</h2>
        <div className="system-info-meta">
          <span className="si-version">v{version}</span>
          <span className="si-build">Build {buildDate}</span>
        </div>
      </div>
      <div className="system-info-body">
        <div className="si-section">
          <h3>Plattform</h3>
          <div className="si-grid">
            <div className="si-row"><span>Version</span><strong>v{version}</strong></div>
            <div className="si-row"><span>Build-Datum</span><strong>{buildDate}</strong></div>
            <div className="si-row"><span>Umgebung</span><strong>Production</strong></div>
            <div className="si-row"><span>Backend</span><strong>Node.js / Express / MySQL</strong></div>
            <div className="si-row"><span>Frontend</span><strong>React (CRA)</strong></div>
          </div>
        </div>
        <div className="si-section">
          <h3>Vollständiger Changelog</h3>
          <div className="changelog-list">
            {changelog.map((entry, i) => {
              const tc = typeConfig[entry.type] || { label: entry.type, color: '#888' };
              return (
                <div key={i} className={`cl-entry ${i === 0 ? 'cl-entry--latest' : ''}`}>
                  <div className="cl-entry-header">
                    <span className="cl-version">v{entry.version}</span>
                    <span className="cl-type-badge" style={{ background: tc.color }}>{tc.label}</span>
                    <span className="cl-date">{entry.date}</span>
                  </div>
                  <div className="cl-title">{entry.title}</div>
                  <ul className="cl-highlights">
                    {entry.highlights.map((h, j) => <li key={j}>{h}</li>)}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Audit-Log Panel ──────────────────────────────────────
function AuditPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/dashboard/audit-log', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => { setLogs(d.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = logs.filter(l =>
    !filter || l.action?.includes(filter) || l.user?.includes(filter) || l.details?.includes(filter)
  );

  return (
    <div className="audit-panel">
      <div className="audit-header">
        <h2>📋 Audit-Log</h2>
        <input
          className="audit-filter"
          placeholder="Filtern…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </div>
      {loading ? (
        <div className="audit-loading">Lade…</div>
      ) : filtered.length === 0 ? (
        <div className="audit-empty">Keine Einträge vorhanden.</div>
      ) : (
        <div className="audit-table-wrap">
          <table className="audit-table">
            <thead>
              <tr><th>Zeit</th><th>Benutzer</th><th>Aktion</th><th>Details</th></tr>
            </thead>
            <tbody>
              {filtered.map((l, i) => (
                <tr key={i}>
                  <td className="audit-td-time">{l.ts ? new Date(l.ts).toLocaleString('de-DE') : '—'}</td>
                  <td>{l.user || '—'}</td>
                  <td><span className="audit-action">{l.action || '—'}</span></td>
                  <td className="audit-td-details">{l.details || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MainDashboard;
